import React, { useState, useCallback, useMemo } from 'react';
import {
    View, Text, ScrollView, TouchableOpacity, StyleSheet,
    Platform, RefreshControl,
    TextInput, Modal, KeyboardAvoidingView, TouchableWithoutFeedback, Keyboard
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import LottieView from 'lottie-react-native';
import { ServiceModuleToast } from '../helpers/ServiceModuleFeedback';
import { useTranslation } from 'react-i18next';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { usePremiumLegacyTheme } from '../helpers/modeScreenTheme';
import { useManagementTheme } from '../helpers/managementTheme';
import { useTimeTracking, formatElapsed } from '../helpers/TimeTrackingContext';
import { useWorkSheets } from '../helpers/WorkSheetContext';
import { useCurrentUser } from '../helpers/useCurrentUser';
import { ChartCard } from '../components/management/ManagementCharts';
import { CapsuleBars, GreetingHeader, HeroKpiCard, IconChip, PillTabs, RankedRow, SectionHeader } from '../components/premium';
import ModeHeaderActions from '../components/ModeHeaderActions';
import {useTopInset} from '../helpers/safeArea';

const WEEK_LABELS = ['H', 'K', 'Sz', 'Cs', 'P', 'Sz', 'V'];

// Resolve name: handles both string and {first, last} objects from API
const resolveName = (name) => {
    if (!name) return '';
    if (typeof name === 'string') return name;
    if (typeof name === 'object') {
        const parts = [name.first, name.last].filter(Boolean);
        return parts.join(' ') || '';
    }
    return String(name);
};

/* ────────────── Helpers & Sub-components ────────────── */

/**
 * Premium surface card: white, hairline border, very soft shadow (dark: navy).
 *
 * @param {object} props
 * @param {React.ReactNode} props.children Card content.
 * @param {object} [props.style] Extra style (padding, border colour…).
 * @returns {JSX.Element} The card.
 */
const GlassCard = ({ children, style }) => {
    const m = useManagementTheme();

    return (
        <View style={[
            styles.glassCard,
            { backgroundColor: m.colors.surface, borderColor: m.colors.border },
            m.shadow(1),
            style
        ]}>
            {children}
        </View>
    );
};

/** Tone of the "invoiced" work-sheet status (the blueprint's violet). */
const BRAND_VIOLET = '#7C3AED';

/**
 * Work-sheet status as a tinted pill.
 *
 * @param {object} props
 * @param {string} props.status `pending` | `in_progress` | `completed` | `invoiced`.
 * @param {Function} props.t i18n translate.
 * @param {object} props.theme Premium legacy theme.
 * @returns {JSX.Element} The pill.
 */
const StatusBadge = ({ status, t, theme }) => {
    const config = {
        pending: { label: t('repair_status_pending'), color: theme.colors.warning },
        in_progress: { label: t('repair_status_in_progress'), color: theme.colors.accent },
        completed: { label: t('repair_status_completed'), color: theme.colors.success },
        invoiced: { label: t('repair_status_invoiced'), color: BRAND_VIOLET },
    };
    const c = config[status] || config.pending;
    return (
        <View style={[styles.statusBadge, { backgroundColor: c.color + '20' }]}>
            <Text style={[styles.statusBadgeText, { color: c.color }]}>{c.label}</Text>
        </View>
    );
};


// New active timer card handling ad-hoc tasks
const ActiveTimerCard = ({ timer, theme, t, onPause, onResume, onBreak, onStop }) => {
    const isRunning = timer.status === 'running';
    const isBreak = timer.status === 'break';
    const isPaused = timer.status === 'paused';

    // Pulse animation effect could be added here with Reanimated, keeping it simple for now
    const statusColor = isRunning ? theme.colors.success : isBreak ? theme.colors.warning : theme.colors.subText;

    return (
        <GlassCard style={[styles.activeTimerCard, { borderColor: statusColor }]}>
            <View style={styles.timerHeader}>
                <View style={styles.timerTitleRow}>
                    <View style={[styles.pulsingDot, { backgroundColor: statusColor }]} />
                    <Text style={[styles.timerTaskTitle, { color: theme.colors.text }]} numberOfLines={1}>
                        {timer.taskName}
                    </Text>
                </View>
                <Text style={[styles.timerElapsedBig, { color: theme.colors.text }]}>
                    {formatElapsed(timer.totalElapsed)}
                </Text>
            </View>

            <View style={styles.timerSubRow}>
                {timer.worksheetId ? (
                    <Text style={[styles.timerContext, { color: theme.colors.primary }]}>
                        {timer.worksheetId}
                    </Text>
                ) : (
                    <Text style={[styles.timerContext, { color: theme.colors.subText }]}>
                        {t('repair_activity_custom')}
                    </Text>
                )}
                {isBreak && (
                    <View style={[styles.breakBadge, { backgroundColor: theme.colors.warning + '20' }]}>
                        <Icon name="coffee" size={14} color={theme.colors.warning} />
                        <Text style={[styles.breakText, { color: theme.colors.warning }]}>{t('repair_timer_break')}</Text>
                    </View>
                )}
            </View>

            <View style={styles.timerControls}>
                {isRunning && (
                    <>
                        <TouchableOpacity style={[styles.controlBtn, { backgroundColor: theme.colors.warning + '15' }]} onPress={() => onPause(timer.taskId)}>
                            <Icon name="pause" size={20} color={theme.colors.warning} />
                        </TouchableOpacity>
                        <TouchableOpacity style={[styles.controlBtn, { backgroundColor: theme.colors.primary + '15' }]} onPress={() => onBreak(timer.taskId)}>
                            <Icon name="coffee" size={20} color={theme.colors.primary} />
                        </TouchableOpacity>
                        <TouchableOpacity style={[styles.controlBtn, { backgroundColor: theme.colors.error + '15' }]} onPress={() => onStop(timer.taskId)}>
                            <Icon name="stop" size={20} color={theme.colors.error} />
                        </TouchableOpacity>
                    </>
                )}
                {(isPaused || isBreak) && (
                    <>
                        <TouchableOpacity style={[styles.resumeBtn, { backgroundColor: theme.colors.success }]} onPress={() => onResume(timer.taskId)}>
                            <Icon name="play" size={20} color="#fff" />
                            <Text style={styles.resumeText}>{t('repair_timer_resume')}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity style={[styles.controlBtn, { backgroundColor: theme.colors.error + '15' }]} onPress={() => onStop(timer.taskId)}>
                            <Icon name="stop" size={20} color={theme.colors.error} />
                        </TouchableOpacity>
                    </>
                )}
            </View>
        </GlassCard>
    );
};

/* ────────────── Main Screen ────────────── */

/**
 * Service (repairman) dashboard: personal header, one orange hero with the
 * work-sheet counters, active timers, the week at a glance (capsule bars),
 * quick timer presets and the latest work sheets.
 *
 * @returns {JSX.Element} The dashboard.
 */
const RepairmanDashboardScreen = () => {
    const { t, i18n } = useTranslation();
    const { theme } = usePremiumLegacyTheme();
    const m = useManagementTheme();
    const navigation = useNavigation();
    const { getActiveTimers, pauseTimer, resumeTimer, startBreak, endBreak, stopTimer, startAdHocTimer } = useTimeTracking();
    const { worksheets, fetchWorksheets } = useWorkSheets();
    const topInset = useTopInset();
    const { user: currentUser, firstName } = useCurrentUser();
    const [refreshing, setRefreshing] = useState(false);
    const [customActivityModal, setCustomActivityModal] = useState(false);
    const [customActivityName, setCustomActivityName] = useState('');
    // Which job status the week chart plots ('all' = every status summed).
    const [weekFilter, setWeekFilter] = useState('all');

    const activeTimers = getActiveTimers(); // returns array
    const sortedTimers = activeTimers.sort((a, b) => b.startedAt - a.startedAt); // Newest first

    // Time-of-day greeting for the header eyebrow ("Jó reggelt, Ferenc"); same keys as the management dashboard.
    const greeting = useMemo(() => {
        const hour = new Date().getHours();
        if (hour >= 5 && hour < 11) return t('mgmt_dash_greeting_morning', { defaultValue: 'Jó reggelt' });
        if (hour >= 11 && hour < 18) return t('mgmt_dash_greeting_day', { defaultValue: 'Szép napot' });

        return t('mgmt_dash_greeting_evening', { defaultValue: 'Jó estét' });
    }, [t]);
    const greetingLine = firstName
        ? t('mgmt_dash_greeting_named', { greeting, name: firstName, defaultValue: '{{greeting}}, {{name}}' })
        : greeting;
    const dateLine = useMemo(() => {
        const text = new Date().toLocaleDateString(i18n.language, { weekday: 'long', month: 'long', day: 'numeric' });

        return text.charAt(0).toUpperCase() + text.slice(1);
    }, [i18n.language]);

    // Ensure dashboard cards always reflect edits made in detail/list screens.
    useFocusEffect(
        useCallback(() => {
            fetchWorksheets();
        }, [fetchWorksheets])
    );

    const onRefresh = useCallback(async () => {
        setRefreshing(true);
        try {
            await fetchWorksheets();
        } finally {
            setRefreshing(false);
        }
    }, [fetchWorksheets]);

    // Keep dashboard aligned with the worksheet list by using the full dataset.
    const dashboardJobs = useMemo(() => {
        const toMillis = (value) => {
            const parsed = Date.parse(value || '');
            return Number.isNaN(parsed) ? 0 : parsed;
        };

        return [...worksheets].sort((a, b) => {
            const bTs = toMillis(b.updatedAt || b.dueDate || b.createdAt);
            const aTs = toMillis(a.updatedAt || a.dueDate || a.createdAt);
            return bTs - aTs;
        });
    }, [worksheets]);

    const stats = useMemo(() => {
        return {
            total: dashboardJobs.length,
            pending: dashboardJobs.filter(j => j.status === 'pending').length,
            inProgress: dashboardJobs.filter(j => j.status === 'in_progress').length,
            completed: dashboardJobs.filter(j => j.status === 'completed' || j.status === 'invoiced').length,
        };
    }, [dashboardJobs]);

    // Weekly Job Stats (Pending, In Progress, Completed)
    const weeklyJobStats = useMemo(() => {
        const today = new Date();
        const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, ...
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const monday = new Date(today);
        monday.setDate(today.getDate() + mondayOffset);
        monday.setHours(0, 0, 0, 0);

        const stats = {
            pending: [0, 0, 0, 0, 0, 0, 0],
            in_progress: [0, 0, 0, 0, 0, 0, 0],
            completed: [0, 0, 0, 0, 0, 0, 0]
        };

        worksheets.forEach(ws => {
            const wsDate = new Date(ws.dueDate || ws.createdAt);
            if (isNaN(wsDate.getTime())) return;
            const diff = Math.floor((wsDate - monday) / (1000 * 60 * 60 * 24));

            if (diff >= 0 && diff < 7) {
                if (ws.status === 'pending') stats.pending[diff]++;
                else if (ws.status === 'in_progress') stats.in_progress[diff]++;
                else if (ws.status === 'completed' || ws.status === 'invoiced') stats.completed[diff]++;
            }
        });

        return stats;
    }, [worksheets]);

    // Normalise every series to finite numbers so the capsule chart never
    // receives NaN (and the empty state below can tell "no data" from "zeros").
    const chartDatasets = useMemo(() => {
        const toFinite = (series) => (Array.isArray(series) ? series : [])
            .map((value) => (Number.isFinite(Number(value)) ? Number(value) : 0));
        return {
            pending: toFinite(weeklyJobStats.pending),
            in_progress: toFinite(weeklyJobStats.in_progress),
            completed: toFinite(weeklyJobStats.completed),
        };
    }, [weeklyJobStats]);

    const hasChartData = useMemo(() => {
        const total = [
            ...chartDatasets.pending,
            ...chartDatasets.in_progress,
            ...chartDatasets.completed,
        ].reduce((sum, value) => sum + value, 0);
        return total > 0;
    }, [chartDatasets]);

    // Capsule columns for the selected status (Mon → Sun), memoised so the
    // chart only re-animates when the data or the filter really changes.
    const weekBars = useMemo(() => WEEK_LABELS.map((label, index) => ({
        label,
        value: weekFilter === 'all'
            ? chartDatasets.pending[index] + chartDatasets.in_progress[index] + chartDatasets.completed[index]
            : chartDatasets[weekFilter][index],
    })), [chartDatasets, weekFilter]);

    // Quick Timer Presets
    const quickTimers = [
        { id: 'coffee', label: t('repair_activity_coffee'), icon: 'coffee-outline', tone: 'warning' },
        { id: 'lunch', label: t('repair_activity_lunch'), icon: 'food-outline', tone: 'primary' },
        { id: 'travel', label: t('repair_activity_travel'), icon: 'car-outline', tone: 'violet' },
        { id: 'admin', label: t('repair_activity_admin'), icon: 'file-document-edit-outline', tone: 'success' },
        { id: 'custom', label: t('repair_start_custom'), icon: 'plus', tone: 'neutral' },
    ];

    const handleQuickTimer = (type) => {
        if (type.id === 'custom') {
            setCustomActivityName('');
            setCustomActivityModal(true);
        } else {
            startAdHocTimer(type.label);
            ServiceModuleToast.success(t('repair_timer_started'), type.label);
        }
    };

    const startCustomActivity = () => {
        if (customActivityName.trim()) {
            startAdHocTimer(customActivityName.trim());
            setCustomActivityModal(false);
            ServiceModuleToast.success(t('repair_timer_started'), customActivityName.trim());
        }
    };

    const handleTimerActions = {
        pause: pauseTimer,
        resume: (id) => {
            const timer = activeTimers.find(t => t.taskId === id);
            if (timer && timer.status === 'break') endBreak(id);
            else resumeTimer(id);
        },
        break: (id) => startBreak(id, t('repair_quick_break', { defaultValue: 'Gyors szünet' })),
        stop: stopTimer
    };

    const statusTone = (status) => (status === 'in_progress' ? 'info' : status === 'completed' ? 'success' : status === 'invoiced' ? 'violet' : 'warning');
    const openWorksheets = () => navigation.navigate('WorkSheetListScreen');

    // Frosted hero tiles: the other real counters; the timer tile only exists while a timer runs.
    const heroTiles = [
        { key: 'inProgress', icon: 'progress-wrench', label: t('repair_in_progress') || 'Folyamatban', value: stats.inProgress },
        { key: 'pending', icon: 'clock-outline', label: t('repair_status_pending') || 'Várakozik', value: stats.pending },
        { key: 'completed', icon: 'checkbox-marked-circle-outline', label: t('repair_completed') || 'Kész', value: stats.completed },
        ...(sortedTimers.length > 0 ? [{ key: 'timers', icon: 'timer-outline', label: t('repair_active_timers'), value: sortedTimers.length }] : []),
    ];

    const weekTabs = [
        { value: 'all', label: t('repair_total') },
        { value: 'pending', label: t('repair_status_pending') },
        { value: 'in_progress', label: t('repair_status_in_progress') },
        { value: 'completed', label: t('repair_status_completed') },
    ];

    return (
        <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
            <ScrollView
                style={styles.scrollView}
                contentContainerStyle={[styles.scrollContent, {paddingTop: topInset + 12}]}
                showsVerticalScrollIndicator={false}
                refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={m.colors.primary} />}
            >
                {/* ─── Header ─── */}
                <GreetingHeader
                    user={currentUser}
                    onAvatarPress={() => navigation.navigate('SettingsScreen')}
                    eyebrow={greetingLine}
                    title={t('repair_dashboard') || 'Áttekintés'}
                    subtitle={dateLine}
                    chipLabel={t('repair_technician')}
                    chipIcon="wrench-outline"
                    right={<ModeHeaderActions />}
                />

                {/* ─── KPI Overview (the one orange highlight) ─── */}
                <HeroKpiCard
                    label={t('repair_total_jobs') || 'Összes'}
                    value={stats.total}
                    onPress={openWorksheets}
                    tiles={heroTiles}
                />

                {/* ─── Active Timers ─── */}
                {sortedTimers.length > 0 && (
                    <View style={styles.activeTimersSection}>
                        <SectionHeader title={`${t('repair_active_timers')} (${sortedTimers.length})`} />
                        {sortedTimers.map((timer) => (
                            <ActiveTimerCard
                                key={timer.taskId}
                                timer={timer}
                                theme={theme}
                                t={t}
                                onPause={handleTimerActions.pause}
                                onResume={handleTimerActions.resume}
                                onBreak={handleTimerActions.break}
                                onStop={handleTimerActions.stop}
                            />
                        ))}
                    </View>
                )}

                {/* ─── Weekly Job Status Chart ─── */}
                <View style={styles.chartSpacer} />
                <ChartCard icon="calendar-week" tone="primary" title={t('repair_weekly_jobs') || 'Heti Munkák'}>
                    {hasChartData ? (
                        <>
                            <View style={styles.weekTabs}>
                                <PillTabs options={weekTabs} value={weekFilter} onChange={setWeekFilter} scroll />
                            </View>
                            <CapsuleBars data={weekBars} height={150} />
                        </>
                    ) : (
                        <View style={styles.chartEmptyState}>
                            <LottieView
                                source={require('../../assets/animations/no-data-here.json')}
                                autoPlay
                                loop
                                style={styles.emptyLottie}
                            />
                            <Text style={{ color: theme.colors.subText, marginTop: 8, fontSize: 14, textAlign: 'center' }}>
                                {t('repair_no_worksheets') || 'Nincs megjeleníthető adat'}
                            </Text>
                        </View>
                    )}
                </ChartCard>

                {/* ─── Quick Timer Actions ─── */}
                <SectionHeader title={t('repair_quick_timer_title')} />
                <View style={styles.quickTimerWrap}>
                    {quickTimers.map((qt) => (
                        <TouchableOpacity
                            key={qt.id}
                            style={[styles.quickTimerChip, { backgroundColor: theme.colors.card, borderColor: theme.colors.border }]}
                            onPress={() => handleQuickTimer(qt)}
                        >
                            <IconChip icon={qt.icon} tone={qt.tone} size={32} round style={styles.quickTimerIcon} />
                            <Text style={[styles.quickTimerText, { color: theme.colors.text }]}>{qt.label}</Text>
                        </TouchableOpacity>
                    ))}
                </View>

                {/* ─── Recent Jobs ─── */}
                <SectionHeader
                    title={t('repair_worksheets') || t('repair_todays_jobs')}
                    actionLabel={t('repair_see_all')}
                    onPress={openWorksheets}
                />

                <View>
                    {dashboardJobs.length === 0 ? (
                        <GlassCard style={styles.jobCard}>
                            <View style={{ alignItems: 'center', paddingVertical: 20 }}>
                                <Icon name="calendar-blank-outline" size={40} color={theme.colors.subText} />
                                <Text style={{ color: theme.colors.subText, marginTop: 8, fontSize: 14 }}>
                                    {t('repair_no_worksheets') || t('repair_no_jobs_today') || 'Nincs munkalap'}
                                </Text>
                            </View>
                        </GlassCard>
                    ) : dashboardJobs.slice(0, 6).map((job) => (
                        <RankedRow
                            key={job._id}
                            icon="clipboard-text-outline"
                            tone={statusTone(job.status)}
                            title={resolveName(job.car) || t('repair_no_car')}
                            subtitle={resolveName(job.customer) || t('repair_no_customer')}
                            badge={<StatusBadge status={job.status} t={t} theme={theme} />}
                            meta={job.scheduledTime || '--:--'}
                            metaCaption={`${job.estimatedHours || 0}h`}
                            onPress={() => navigation.navigate('WorkSheetDetailScreen', { worksheet: job })}
                        />
                    ))}
                </View>
                <View style={{ height: 120 }} />
            </ScrollView>

            {/* Custom Activity Modal (solid sheet) */}
            <Modal transparent visible={customActivityModal} animationType="fade">
                <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
                    <View style={styles.modalOverlay}>
                        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={styles.modalContentWrap}>
                            <View style={[styles.modalContent, { backgroundColor: m.colors.surface, borderColor: m.colors.border }]}>
                                <Text style={[styles.modalTitle, { color: theme.colors.text }]}>{t('repair_enter_activity')}</Text>
                                <TextInput
                                    style={[styles.modalInput, {
                                        backgroundColor: theme.colors.inputBackground,
                                        color: theme.colors.text,
                                        borderColor: theme.colors.border
                                    }]}
                                    placeholder={t('repair_activity_custom')}
                                    placeholderTextColor={theme.colors.placeholder}
                                    value={customActivityName}
                                    onChangeText={setCustomActivityName}
                                    autoFocus
                                />
                                <View style={[styles.modalButtons, { borderTopColor: theme.colors.border }]}>
                                    <TouchableOpacity
                                        style={[styles.modalBtn, { borderRightWidth: 1, borderRightColor: theme.colors.border }]}
                                        onPress={() => setCustomActivityModal(false)}
                                    >
                                        <Text style={[styles.modalBtnText, { color: theme.colors.subText }]}>{t('common_cancel')}</Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity style={styles.modalBtn} onPress={startCustomActivity}>
                                        <Text style={[styles.modalBtnText, { color: theme.colors.primary, fontWeight: '700' }]}>{t('repair_start_custom')}</Text>
                                    </TouchableOpacity>
                                </View>
                            </View>
                        </KeyboardAvoidingView>
                    </View>
                </TouchableWithoutFeedback>
            </Modal>
        </View>
    );
};

const styles = StyleSheet.create({
    container: { flex: 1 },
    scrollView: { flex: 1 },
    scrollContent: { padding: 20 }, // paddingTop applied dynamically via useTopInset()

    // Quick Timer
    quickTimerWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 8 },
    quickTimerChip: {
        flexDirection: 'row', alignItems: 'center', padding: 6, paddingRight: 16,
        borderRadius: 999, borderWidth: 1,
    },
    quickTimerIcon: { marginRight: 10 },
    quickTimerText: { fontWeight: '700', fontSize: 13 },

    // Surface card default
    glassCard: { borderRadius: 26, borderWidth: 1, overflow: 'hidden' },

    // Active Timer
    activeTimersSection: { marginBottom: 4 },
    activeTimerCard: { padding: 20, borderWidth: 2, marginBottom: 10 },
    timerHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
    timerTitleRow: { flexDirection: 'row', alignItems: 'center', flex: 1 },
    pulsingDot: { width: 8, height: 8, borderRadius: 4, marginRight: 10 },
    timerTaskTitle: { fontSize: 16, fontWeight: '700', flex: 1 },
    timerElapsedBig: { fontSize: 22, fontWeight: '800', letterSpacing: -0.5, fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace' },
    timerSubRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
    timerContext: { fontSize: 13, fontWeight: '600' },
    breakBadge: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999, gap: 4 },
    breakText: { fontSize: 12, fontWeight: '700' },
    timerControls: { flexDirection: 'row', gap: 10 },
    controlBtn: { width: 44, height: 44, borderRadius: 22, justifyContent: 'center', alignItems: 'center' },
    resumeBtn: { flex: 1, height: 44, borderRadius: 22, flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 8 },
    resumeText: { color: '#fff', fontWeight: '700', fontSize: 15 },

    // Chart
    chartSpacer: { height: 8 },
    weekTabs: { marginBottom: 8, marginLeft: -6 },
    chartEmptyState: { height: 200, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 20 },
    emptyLottie: { width: 140, height: 140 },

    // Jobs
    jobCard: { padding: 16, marginBottom: 0 },
    statusBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999 },
    statusBadgeText: { fontSize: 11, fontWeight: '700' },

    // Modal
    modalOverlay: { flex: 1, backgroundColor: 'rgba(15,23,42,0.5)', justifyContent: 'center', padding: 20 },
    modalContentWrap: { width: '100%' },
    modalContent: { borderRadius: 28, borderWidth: 1, overflow: 'hidden' },
    modalTitle: { textAlign: 'center', fontSize: 18, fontWeight: '800', marginTop: 22, marginBottom: 15 },
    modalInput: { marginHorizontal: 20, paddingHorizontal: 16, paddingVertical: 12, borderRadius: 999, borderWidth: 1, fontSize: 16, marginBottom: 20 },
    modalButtons: { flexDirection: 'row', borderTopWidth: 1 },
    modalBtn: { flex: 1, padding: 16, alignItems: 'center' },
    modalBtnText: { fontSize: 16 },
});

export default RepairmanDashboardScreen;
